'use client';

import { classes } from '@/next-utils/css-utils/scss-utils';
import { useLocaleMessage } from '@/react/hooks/useLocaleMessage';
import { Nullable } from '@/type-utils';
import { InvalidArgumentError } from '@/utils/errors/InvalidArgumentError';
import { use, useMemo, type FC, type JSX } from 'react';
import { createClickOutsideRef } from '@/react/utils/ref-utils/createClickOutsideRef';
import { Button } from '../../../../core-ui/Button';
import { Icon, IconTypes } from '../../../../core-ui/Icon';
import { NotificationContext } from '../../controller';
import NotificationType from '../../NotificationType';
import {
  IBasicNotification,
  ICustomNotification,
  IQueuedNotification
} from '../../types';
import BasicNotificationBody from '../BasicNotificationBody';

import S from './styles.base.module.scss';
import { general_closeNotification } from "@/lang/__generated__/ahnu/general_closeNotification";

/** Describes the props for the {@link Notification} component. */
export interface INotificationProps {
  /** Notification to render. */
  notification: Nullable<IQueuedNotification>;
}

/**
 * Renders a queued notification.
 * @throws An {@link InvalidArgumentError} when receiving a notification of an invalid type.
 */
const Notification: FC<INotificationProps> = ({ notification }) => {
  const [msg] = useLocaleMessage();
  const { deleteNotification } = use(NotificationContext);

  let content: Nullable<JSX.Element> = null;
  if (notification)
    switch (notification.type) {
      case NotificationType.Basic: {
        content = (
          <BasicNotificationBody
            notification={notification as IBasicNotification}
          />
        );
        break;
      }

      case NotificationType.Custom: {
        content = (notification as ICustomNotification).content;
        break;
      }

      default: {
        throw new InvalidArgumentError(
          `Received notification with invalid type: ${notification.type}`
        );
      }
    }

  const deleteOnOutsideClick = useMemo(
    () =>
      createClickOutsideRef<HTMLDivElement>(
        () => notification && deleteNotification(notification.uuid)
      ),
    [notification, deleteNotification]
  );

  return (
    <>
      {content && (
        <div className={S.headsUpNotification} ref={deleteOnOutsideClick}>
          <div
            className={classes(
              S.content,
              notification?.options?.isError ? S.error : '',
              { [S.visible]: !!notification && !!content }
            )}
          >
            <Button
              className={S.close}
              onClick={() => {
                if (notification) {
                  deleteNotification(notification.uuid);
                }
              }}
              variant="text"
              ariaLabel={msg(general_closeNotification)}
            >
              <Icon className={S.closeIcon} icon={IconTypes.Close} />
            </Button>
            {content}
          </div>
        </div>
      )}
    </>
  );
};

export default Notification;
